import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useEffect, useRef, useCallback, memo, useState } from "react";
import { FormActions } from "./FormActions";
import { useTranslation } from "react-i18next";

interface FormTabsProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  children: React.ReactNode;
  onCancel: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isSubmitting: boolean;
  isCreateMode?: boolean;
}

export const FormTabs = memo(({
  activeTab,
  setActiveTab,
  children,
  onCancel,
  onSubmit,
  isSubmitting,
  isCreateMode = false
}: FormTabsProps) => {
  const { t } = useTranslation();

  const initialRender = useRef(true);
  const formRef = useRef<HTMLFormElement>(null);
  const [error, setError] = useState<string | null>(null);

  const tabOrder = ["basic", "details", "qualifications", "metadata"];

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    }
  }, []);

  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value);
    setError(null);
  }, [setActiveTab]);

  const validateForm = useCallback(() => {
    if (!formRef.current) return true;

    const requiredFields = formRef.current.querySelectorAll('input[required], textarea[required], select[required]');

    for (const field of Array.from(requiredFields)) {
      const input = field as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;
      if (!input.value.trim()) {
        input.focus();
        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const label = input.closest('div')?.querySelector('label')?.textContent?.replace(' *', '') || 'Field';
        setError(`${label} ${t("common.isRequired")}`);
        return false;
      }
    }

    if (activeTab === "basic") {
      const companyField = formRef.current.querySelector('input[name="companyId"]');
      if (companyField) {
        const companyIdValue = (companyField as HTMLInputElement).value;
        if (!companyIdValue) {
          setError(t("jobs.formWizard.companyRequired"));
          document.querySelector('#company-selector')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return false;
        }
      }
    }

    setError(null);
    return true;
  }, [activeTab, t]);

  const handleFormSubmit = useCallback((e: any) => {
    e.preventDefault();

    let isExplicitSave = false;
    let isSubmitButtonClick = false;

    if (e.nativeEvent?._isSubmitButtonClick) {
      isSubmitButtonClick = true;
    }

    if (e.nativeEvent?._isExplicitSave)
      isExplicitSave = true;

    if (isExplicitSave || isSubmitButtonClick) {
      if (validateForm()) {
        onSubmit(e);
      }
    }
  }, [onSubmit, validateForm]);

  const handleNext = useCallback((e: React.MouseEvent) => {
    const currentIndex = tabOrder.indexOf(activeTab);

    if (activeTab === "basic" && !validateForm()) {
      return;
    }

    if (currentIndex < tabOrder.length - 1) {
      e.preventDefault();
      setActiveTab(tabOrder[currentIndex + 1]);
      setError(null);
    }
  }, [activeTab, setActiveTab, validateForm, tabOrder]);

  const handlePrevious = useCallback(() => {
    const currentIndex = tabOrder.indexOf(activeTab);
    if (currentIndex > 0) {
      setActiveTab(tabOrder[currentIndex - 1]);
      setError(null);
    }
  }, [activeTab, setActiveTab, tabOrder]);

  const handleSave = useCallback(() => {
    if (validateForm()) {
      const event = new CustomEvent('submit', { bubbles: true, cancelable: true }) as unknown as React.FormEvent;
      (event as any)._isExplicitSave = true;

      if (formRef.current) {
        formRef.current.dispatchEvent(event as unknown as Event);
      }
    }
  }, [validateForm]);

  const isLastTab = activeTab === tabOrder[tabOrder.length - 1];

  const contentHeight = "h-[calc(100vh-240px)]";

  return (
    <form
      ref={formRef}
      onSubmit={handleFormSubmit}
      className="space-y-4 flex flex-col h-full relative"
    >
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full flex-grow flex flex-col">
        <TabsList className="grid grid-cols-4 mb-6">
          <TabsTrigger value="basic">{t("jobs.formWizard.basicInfo")}</TabsTrigger>
          <TabsTrigger value="details">{t("jobs.formWizard.jobDetails")}</TabsTrigger>
          <TabsTrigger value="qualifications">{t("jobs.formWizard.qualifications")}</TabsTrigger>
          <TabsTrigger value="metadata">{t("jobs.formWizard.additionalInfo")}</TabsTrigger>
        </TabsList>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" role="alert">
            <p>{error}</p>
          </div>
        )}

        <div className={`flex-grow relative overflow-hidden ${contentHeight}`}>
          <ScrollArea className="h-full w-full pr-4 pb-20">
            {children}
          </ScrollArea>
        </div>
      </Tabs>

      <FormActions
        activeTab={activeTab}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onSave={handleSave}
        isLastTab={isLastTab}
        isCreateMode={isCreateMode}
      />
    </form>
  );
});

FormTabs.displayName = "FormTabs";
